import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isGuest: boolean;
  isAdmin: boolean;
}

interface AuthContextType extends AuthState {
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  continueAsGuest: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function checkAdmin(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  return !!data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isGuest: false,
    isAdmin: false,
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setState((prev) => ({
        user: session?.user ?? null,
        session,
        isLoading: false,
        isGuest: !session ? prev.isGuest : false,
        isAdmin: prev.isAdmin,
      }));

      if (event === "SIGNED_IN" && session?.user) {
        localStorage.removeItem("dos_guest");
        // Defer role check
        setTimeout(() => {
          checkAdmin(session.user.id).then((admin) => {
            setState((prev) => ({ ...prev, isAdmin: admin }));
          });
        }, 0);
      } else if (event === "SIGNED_OUT") {
        setState((prev) => ({ ...prev, isAdmin: false }));
      }
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const wasGuest = localStorage.getItem("dos_guest") === "true";
      const admin = session?.user ? await checkAdmin(session.user.id) : false;
      setState({
        user: session?.user ?? null,
        session,
        isLoading: false,
        isGuest: !session && wasGuest,
        isAdmin: admin,
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    if (!error) localStorage.removeItem("dos_guest");
    return { error: error?.message ?? null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) localStorage.removeItem("dos_guest");
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("dos_guest");
    setState({ user: null, session: null, isLoading: false, isGuest: false, isAdmin: false });
  };

  const continueAsGuest = () => {
    localStorage.setItem("dos_guest", "true");
    setState((prev) => ({ ...prev, isGuest: true }));
  };

  return (
    <AuthContext.Provider value={{ ...state, signUp, signIn, signOut, continueAsGuest }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
